import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import useEquirectangularStore from '@/store/viewer';

interface CameraControlDirectionDragProps {
  setDrag?: () => void; // 선택적 props
}

const CameraControlDirectionDrag = ({
  setDrag,
}: CameraControlDirectionDragProps) => {
  const setIsDragging = useEquirectangularStore((state) => state.setIsDragging);
  const { camera, gl } = useThree();

  useEffect(() => {
    let startX = 0;
    let startY = 0;
    const startQuaternion = new THREE.Quaternion().copy(camera.quaternion);

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      rotateCamera(deltaX, deltaY);

      setIsDragging(true);
    };

    const handleMouseDown = (event: MouseEvent) => {
      startX = event.clientX;
      startY = event.clientY;
      startQuaternion.copy(camera.quaternion);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 1) {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        startQuaternion.copy(camera.quaternion);
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length === 1) {
        const deltaX = event.touches[0].clientX - startX;
        const deltaY = event.touches[0].clientY - startY;
        rotateCamera(deltaX, deltaY);
        setIsDragging(true);
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    const rotateCamera = (deltaX: number, deltaY: number) => {
      const sensitivity = 0.005;

      const euler = new THREE.Euler(0, 0, 0, 'YXZ');
      euler.setFromQuaternion(startQuaternion);

      euler.y += deltaX * sensitivity;
      euler.x += deltaY * sensitivity;
      euler.x = Math.max(Math.min(euler.x, Math.PI / 2), -Math.PI / 6);

      camera.quaternion.setFromEuler(euler);
    };

    gl.domElement.addEventListener('mousedown', handleMouseDown);
    gl.domElement.addEventListener('touchstart', handleTouchStart);

    return () => {
      gl.domElement.removeEventListener('mousedown', handleMouseDown);
      gl.domElement.removeEventListener('touchstart', handleTouchStart);
    };
  }, [camera, gl]);

  return null;
};

const CameraControlAutoDrag = () => {
  const { camera } = useThree();

  useEffect(() => {
    let animationFrameId: number;
    const rotationSpeed = 0.002; // 회전 속도 (조정 가능)

    const autoRotateCamera = () => {
      // Y축을 기준으로 회전
      camera.rotation.y += rotationSpeed;

      // 재귀적으로 애니메이션 호출
      animationFrameId = requestAnimationFrame(autoRotateCamera);
    };

    // 애니메이션 시작
    autoRotateCamera();

    // 클린업: 컴포넌트가 언마운트될 때 애니메이션 정지
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [camera]);

  return null;
};

export { CameraControlDirectionDrag, CameraControlAutoDrag };
